import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Pagination,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import referralService from '../services/referralService';

const ReferralManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [error, setError] = useState('');
  const [processingPending, setProcessingPending] = useState(false);

  // Fetch referrals
  const fetchReferrals = async () => {
    try {
      setLoading(true);
      const params = {
        page: page.toString(),
        limit: '20',
      };
      
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;

      const data = await referralService.getAllReferrals(params);
      setReferrals(data.data.referrals);
      setTotalPages(data.data.pagination.pages);
    } catch (error) {
      setError('Failed to fetch referrals: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch settings
  const fetchSettings = async () => {
    try {
      const data = await referralService.getReferralSettings();
      setSettings(data.data);
      setSettingsForm(data.data);
    } catch (error) {
      setError('Failed to fetch settings: ' + error.message);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const data = await referralService.getReferralStats();
      console.log('Referral Stats:', data.data); // Debug log
      setStats(data.data);
    } catch (error) {
      setError('Failed to fetch stats: ' + error.message);
    }
  };

  // Update settings
  const updateSettings = async () => {
    try {
      const data = await referralService.updateReferralSettings(settingsForm);
      setSettings(data.data);
      setSettingsDialogOpen(false);
      setError('');
    } catch (error) {
      setError('Failed to update settings: ' + error.message);
    }
  };

  // Process pending referrals
  const handleProcessPending = async () => {
    try {
      setProcessingPending(true);
      const result = await referralService.processPendingReferrals();
      
      if (result.success) {
        toast.success(result.message || 'Pending referrals processed successfully');
        // Refresh stats and referrals
        await fetchStats();
        await fetchReferrals();
      } else {
        toast.error(result.message || 'Failed to process pending referrals');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to process pending referrals');
    } finally {
      setProcessingPending(false);
    }
  };

  useEffect(() => {
    fetchReferrals();
    fetchSettings();
    fetchStats();
  }, [page, searchTerm, statusFilter]);

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', label: 'Pending' },
      completed: { color: 'success', label: 'Completed' },
      cancelled: { color: 'error', label: 'Cancelled' },
    };

    const config = statusConfig[status] || { color: 'default', label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount);
  };

  if (loading && !referrals.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Referral Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Debug Info - Remove after testing */}
      {stats && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Debug: pendingWithDeposit = {stats.pendingWithDeposit ?? 'undefined'}, 
            pendingReferrals = {stats.pendingReferrals ?? 'undefined'}
          </Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Referrals
                  </Typography>
                  <Typography variant="h5">
                    {stats?.totalReferrals || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h5">
                    {stats?.completedReferrals || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <StarIcon color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Rewards Given
                  </Typography>
                  <Typography variant="h5">
                    {stats?.totalRewardsGiven || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Deposits
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(stats?.totalDepositAmount || 0)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {stats?.pendingWithDeposit > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ border: '2px solid', borderColor: 'warning.main', bgcolor: 'rgba(255, 152, 0, 0.05)' }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <CheckCircleIcon color="warning" sx={{ mr: 1 }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom>
                        Ready for Rewards
                      </Typography>
                      <Typography variant="h5" color="warning.main" fontWeight="bold">
                        {stats.pendingWithDeposit}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={handleProcessPending}
                    disabled={processingPending}
                    startIcon={processingPending ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                    sx={{ ml: 2 }}
                  >
                    Process
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Pending Referrals Alert */}
      {stats?.pendingWithDeposit > 0 && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleProcessPending}
              disabled={processingPending}
              startIcon={processingPending ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            >
              {processingPending ? 'Processing...' : 'Process Now'}
            </Button>
          }
        >
          <Typography variant="body2">
            <strong>{stats.pendingWithDeposit}</strong> pending referral{stats.pendingWithDeposit !== 1 ? 's' : ''} have met deposit requirements but haven't received rewards yet.
          </Typography>
        </Alert>
      )}

      {/* Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search referrals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setPage(1);
              fetchReferrals();
              fetchStats();
            }}
          >
            Refresh
          </Button>
        </Box>

        <Box display="flex" gap={2} alignItems="center">
          <Button
            variant={stats?.pendingWithDeposit > 0 ? "contained" : "outlined"}
            color="warning"
            startIcon={processingPending ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            onClick={handleProcessPending}
            disabled={processingPending}
          >
            {processingPending 
              ? 'Processing...' 
              : stats?.pendingWithDeposit > 0 
                ? `Process Pending (${stats.pendingWithDeposit})` 
                : 'Process Pending'}
          </Button>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsDialogOpen(true)}
          >
            Settings
          </Button>
        </Box>
      </Box>

      {/* Referrals Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Referrer</TableCell>
              <TableCell>Referred User</TableCell>
              <TableCell>Referral Code</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>First Deposit</TableCell>
              <TableCell>Total Deposits</TableCell>
              <TableCell>Reward Earned</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {referrals.map((referral) => (
              <TableRow key={referral._id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {referral.referrerId?.fullname || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {referral.referrerId?.username || 'N/A'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {referral.referredUserId?.fullname || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {referral.referredUserId?.username || 'N/A'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {referral.referralCode}
                  </Typography>
                </TableCell>
                <TableCell>
                  {getStatusChip(referral.status)}
                </TableCell>
                <TableCell>
                  {referral.firstDepositAmount > 0 ? (
                    <Typography variant="body2">
                      {formatCurrency(referral.firstDepositAmount)}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No deposit
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {referral.totalDeposits} ({formatCurrency(referral.totalDepositAmount)})
                  </Typography>
                </TableCell>
                <TableCell>
                  {referral.rewardEarned ? (
                    <Chip label="Yes" color="success" size="small" />
                  ) : (
                    <Chip label="No" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(referral.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small">
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Referral System Settings</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsForm.enabled || false}
                    onChange={(e) => setSettingsForm({ ...settingsForm, enabled: e.target.checked })}
                  />
                }
                label="Enable Referral System"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Minimum Deposit Amount (₦)"
                type="number"
                value={settingsForm.minimumDepositAmount || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, minimumDepositAmount: Number(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reward Loyalty Points"
                type="number"
                value={settingsForm.rewardLoyaltyPoints || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, rewardLoyaltyPoints: Number(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Referral Code Length"
                type="number"
                value={settingsForm.referralCodeLength || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, referralCodeLength: Number(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Referrals Per User (0 = unlimited)"
                type="number"
                value={settingsForm.maxReferralsPerUser || ''}
                onChange={(e) => setSettingsForm({ ...settingsForm, maxReferralsPerUser: Number(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsForm.allowSelfReferral || false}
                    onChange={(e) => setSettingsForm({ ...settingsForm, allowSelfReferral: e.target.checked })}
                  />
                }
                label="Allow Self Referral"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsForm.requireKYC || false}
                    onChange={(e) => setSettingsForm({ ...settingsForm, requireKYC: e.target.checked })}
                  />
                }
                label="Require KYC for Reward"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Notification Settings
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsForm.notificationSettings?.referralCodeUsed || false}
                    onChange={(e) => setSettingsForm({
                      ...settingsForm,
                      notificationSettings: {
                        ...settingsForm.notificationSettings,
                        referralCodeUsed: e.target.checked
                      }
                    })}
                  />
                }
                label="Referral Code Used"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsForm.notificationSettings?.rewardEarned || false}
                    onChange={(e) => setSettingsForm({
                      ...settingsForm,
                      notificationSettings: {
                        ...settingsForm.notificationSettings,
                        rewardEarned: e.target.checked
                      }
                    })}
                  />
                }
                label="Reward Earned"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settingsForm.notificationSettings?.depositMade || false}
                    onChange={(e) => setSettingsForm({
                      ...settingsForm,
                      notificationSettings: {
                        ...settingsForm.notificationSettings,
                        depositMade: e.target.checked
                      }
                    })}
                  />
                }
                label="Deposit Made"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsDialogOpen(false)}>Cancel</Button>
          <Button onClick={updateSettings} variant="contained">Save Settings</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReferralManagement; 